<script lang="ts" setup>
import { useDebounceFn, useInfiniteScroll } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import type { ComputedRef } from 'vue';
import {
  computed,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import type { TpgChangeEvent } from '@nora-monorepo/grammar-ui';
import {
  GrammarCheckbox,
  GrammarLocalLoader,
  GrammarTabBar,
  GrammarTabItem,
  GrammarText,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';
import type {
  GroupResultDTO,
  UserResultDTO,
} from '@nora-monorepo/nora-api-client';
import { formatName } from '@nora-monorepo/shared';

import AvatarImage from '#ui-shared/components/avatar-image/AvatarImage.vue';
import type { UsersAndGroups } from '#ui-shared/components/member-picker/member-picker-types';
import HoverableUserName from '#ui-shared/components/user-details/HoverableUserName.vue';
import { useSearchStore } from '#ui-shared/stores/search-store';
import { getInitials } from '#ui-shared/utils/get-initials';

enum MemberPickerTab {
  Users = 'users',
  Groups = 'groups',
}

interface MemberSearchPickerProps {
  currentMemberList: UsersAndGroups;
  filterString: string;
  enabled?: boolean;
}

const props = withDefaults(defineProps<MemberSearchPickerProps>(), {
  enabled: true,
});

const emit = defineEmits<{
  'update:filterString': [value: string];
  'toggleUser': [user: UserResultDTO];
  'toggleGroup': [group: GroupResultDTO];
  'selectAllGroups': [shouldSelectAll: boolean, groups: GroupResultDTO[]];
  'selectAllUsers': [shouldSelectAll: boolean, users: UserResultDTO[]];
}>();

const filterString = defineModel<string>('filterString', { required: true });

const { t } = useI18n();
const searchStore = useSearchStore();
const { searchResults } = storeToRefs(searchStore);

const userResultElement = useTemplateRef<HTMLElement>('userResultElement');
const groupResultElement = useTemplateRef<HTMLElement>('groupResultElement');
const selectedTab = ref<MemberPickerTab>(MemberPickerTab.Users);

const members = computed(() => props.currentMemberList ?? { users: [], groups: [] });

const searchIsLoading = computed(() => {
  return (
    searchResults.value.users.searching || searchResults.value.groups.searching
  );
});

const canLoadMoreUsers = computed(() => {
  return (
    searchResults.value.users.results.length
    < searchResults.value.users.pagination.total
  );
});

const canLoadMoreGroups = computed(() => {
  return (
    searchResults.value.groups.results.length
    < searchResults.value.groups.pagination.total
  );
});

const { reset: resetUsers } = useInfiniteScroll(
  userResultElement,
  async () => {
    if (!canLoadMoreUsers.value || searchIsLoading.value) {
      return;
    }

    await searchStore.loadMoreUsers(filterString.value);
  },
  {
    distance: 10,
    canLoadMore: () => canLoadMoreUsers.value,
  },
);

const { reset: resetGroups } = useInfiniteScroll(
  groupResultElement,
  async () => {
    if (!canLoadMoreGroups.value || searchIsLoading.value) {
      return;
    }

    await searchStore.loadMoreGroups(filterString.value);
  },
  {
    distance: 10,
    canLoadMore: () => canLoadMoreGroups.value,
  },
);

const searchDebounced = useDebounceFn(
  async (query: string) => {
    await searchStore.searchUsersAndGroups(query);
  },
  300,
  { maxWait: 1000 },
);

function resetScrollState(): void {
  resetUsers();
  resetGroups();
}

async function searchMembersImmediately(value: string): Promise<void> {
  if (!props.enabled) {
    return;
  }

  resetScrollState();
  await searchStore.searchUsersAndGroups(value);
}

async function searchMembers(value: string): Promise<void> {
  if (!props.enabled) {
    return;
  }

  resetScrollState();
  await searchDebounced(value);
}

async function onFilterStringInput(value: string): Promise<void> {
  await searchMembers(value);
}

watch(
  () => props.enabled,
  async (enabled) => {
    if (!enabled) {
      return;
    }

    await searchMembersImmediately(filterString.value);
  },
);

onMounted(() => {
  if (!props.enabled) {
    return;
  }

  searchMembersImmediately(filterString.value);
});

function onTabChange(tab: TpgChangeEvent<MemberPickerTab>): void {
  if (!tab.value) {
    return;
  }

  selectedTab.value = tab.value;
}

const isAllSelected: ComputedRef<boolean> = computed(() => {
  if (selectedTab.value === MemberPickerTab.Users) {
    return searchResults.value.users.results.length > 0
      && searchResults.value.users.results.every(result => members.value.users.some(user => user.user.id === result.id));
  }

  const groupSearchResults = searchResults.value.groups.results;
  return groupSearchResults.length > 0
    && groupSearchResults.every(result => members.value.groups.some(group => group.group.id === result.id));
});

const areSomeSelected: ComputedRef<boolean> = computed(() => {
  if (selectedTab.value === MemberPickerTab.Users) {
    return searchResults.value.users.results.some(result => members.value.users.some(user => user.user.id === result.id));
  }

  return searchResults.value.groups.results.some(result => members.value.groups.some(group => group.group.id === result.id));
});

function onSelectAllClick(): void {
  if (selectedTab.value === MemberPickerTab.Users) {
    emit('selectAllUsers', !isAllSelected.value, searchResults.value.users.results);
    return;
  }

  emit('selectAllGroups', !isAllSelected.value, searchResults.value.groups.results);
}
</script>

<template>
  <span class="flex flex-col">
    <GrammarTextField
      v-model:value="filterString"
      :label="t('conversationDialog.filterMembers')"
      :placeholder="t('conversationDialog.filterMembersPlaceholder')"
      :form-associated="false"
      :disabled="!props.enabled"
      @input="onFilterStringInput"
    />
  </span>

  <div class="h-full flex flex-col py-2 text-light-label-secondary dark:text-dark-label-secondary">
    <GrammarTabBar :selected-value="selectedTab" @change="onTabChange">
      <GrammarTabItem label="Users" :value="MemberPickerTab.Users" />
      <GrammarTabItem label="Groups" :value="MemberPickerTab.Groups" />
    </GrammarTabBar>

    <div class="flex justify-end">
      <GrammarCheckbox
        label="Select all"
        :form-associated="false"
        :checked="isAllSelected"
        :indeterminate="areSomeSelected && !isAllSelected"
        :disabled="!props.enabled || searchIsLoading"
        @change="onSelectAllClick"
      />
    </div>

    <div class="relative min-h-0 flex-1">
      <div
        v-if="searchIsLoading"
        class="absolute inset-0 z-10 flex items-center justify-center"
      >
        <GrammarLocalLoader />
      </div>

      <div
        v-if="selectedTab === MemberPickerTab.Users"
        ref="userResultElement"
        class="h-full overflow-y-auto pr-2"
      >
        <div
          v-for="userSearchResult in searchResults.users.results"
          :key="userSearchResult.id"
          class="flex flex-row items-center border-b-light-separator-transparency dark:border-b-dark-separator-transparency border-b last:border-b-0"
          :class="{ 'cursor-pointer': props.enabled }"
          @click="props.enabled && emit('toggleUser', userSearchResult)"
        >
          <label class="flex flex-row items-center w-full" :class="{ 'cursor-pointer': props.enabled }">
            <AvatarImage
              :id="userSearchResult.id"
              class="mr-2"
              :text="getInitials(userSearchResult)"
            />
            <HoverableUserName class="mx-1" :user="userSearchResult" />
          </label>

          <GrammarCheckbox
            :form-associated="false"
            :checked="members.users.some((user) => user.user.id === userSearchResult.id)"
            :disabled="!props.enabled || searchIsLoading"
          />
        </div>
      </div>

      <div
        v-if="selectedTab === MemberPickerTab.Groups"
        ref="groupResultElement"
        class="h-full overflow-y-auto pr-2"
      >
        <div
          v-for="item in searchResults.groups.results"
          :key="item.id"
          class="flex flex-row items-center border-b-light-separator-transparency dark:border-b-dark-separator-transparency border-b last:border-b-0"
          :class="{ 'cursor-pointer': props.enabled }"
          @click="props.enabled && emit('toggleGroup', item)"
        >
          <label class="flex flex-row items-center w-full" :class="{ 'cursor-pointer': props.enabled }">
            <AvatarImage :id="item.id" class="mr-2" icon="groups" />
            <GrammarText :text="formatName(item) ?? ''" class="mx-1" />
          </label>

          <GrammarCheckbox
            :form-associated="false"
            :checked="members.groups.some((group) => group.group.id === item.id)"
            :disabled="!props.enabled || searchIsLoading"
          />
        </div>
      </div>
    </div>
  </div>
</template>
